/**
 * Recipe Fallback Images - Automatische Kategorie-Erkennung für Default-Bilder
 * i18n-fähig: Erkennt Keywords in DE, EN und weiteren Sprachen
 */

// Import der Default-Bilder
import breakfastImage from '../assets/images/breakfast.png';
import mainImage from '../assets/images/main.png';
import healthyImage from '../assets/images/healthy.png';
import dessertImage from '../assets/images/dessert.png';
import snacksImage from '../assets/images/snacks.png';
import partyImage from '../assets/images/party.png';

// Bild-Mapping
const CATEGORY_IMAGES = {
  party: partyImage,
  breakfast: breakfastImage,
  dessert: dessertImage,
  healthy: healthyImage,
  snacks: snacksImage,
  main: mainImage,
};

// Mehrsprachige Keywords pro Kategorie (Priorität durch Reihenfolge)
// Priorität: party > breakfast > dessert > healthy > snacks > main
const CATEGORY_KEYWORDS = {
  party: [
    // Deutsch
    'party', 'buffet', 'gäste', 'gaeste', 'grill', 'bbq', 'geburtstag',
    'feier', 'silvester', 'weihnacht', 'ostern', 'fest', 'festlich',
    'fingerfood', 'häppchen', 'haeppchen', 'aperitif', 'bowle', 'cocktail',
    // Englisch
    'celebration', 'birthday', 'christmas', 'easter', 'new year',
    'festive', 'holiday', 'gathering', 'potluck',
    // Französisch
    'fête', 'fete', 'anniversaire', 'noël', 'noel',
    // Spanisch
    'fiesta', 'cumpleaños', 'cumpleanos', 'navidad',
    // Italienisch
    'festa', 'compleanno', 'natale',
  ],

  breakfast: [
    // Deutsch
    'frühstück', 'fruehstueck', 'müsli', 'muesli', 'porridge', 'overnight',
    'toast', 'brötchen', 'broetchen', 'croissant', 'marmelade', 'konfitüre',
    'konfituere', 'ei', 'eier', 'rührei', 'ruehrei', 'spiegelei', 'omelette',
    'pancake', 'pfannkuchen', 'waffel', 'french toast', 'granola', 'smoothie bowl',
    'açaí', 'acai', 'brunch', 'haferflocken', 'haferbrei',
    // Englisch
    'breakfast', 'morning', 'cereal', 'oatmeal', 'eggs', 'bacon',
    'scrambled', 'sunny side', 'benedict', 'avocado toast',
    // Französisch
    'petit déjeuner', 'petit dejeuner', 'brioche',
    // Spanisch
    'desayuno', 'tostada',
    // Italienisch
    'colazione', 'cornetto',
  ],

  dessert: [
    // Deutsch
    'dessert', 'nachtisch', 'kuchen', 'torte', 'muffin', 'cookie', 'keks',
    'schokolade', 'schoko', 'eis', 'pudding', 'creme', 'crème', 'mousse',
    'tiramisu', 'panna cotta', 'brownie', 'cheesecake', 'käsekuchen',
    'kaesekuchen', 'apfelkuchen', 'obstkuchen', 'sahne', 'süß', 'suess',
    'bonbon', 'praline', 'trueffel', 'trüffel', 'macaron', 'eclair',
    'tarte', 'pie', 'cobbler', 'crumble', 'streusel',
    // Englisch
    'sweet', 'cake', 'pastry', 'candy', 'chocolate', 'ice cream',
    'frozen', 'custard', 'caramel', 'fudge', 'cupcake', 'donut', 'doughnut',
    // Französisch
    'gâteau', 'gateau', 'pâtisserie', 'patisserie', 'crêpe', 'crepe',
    // Spanisch
    'postre', 'dulce', 'helado', 'pastel', 'flan',
    // Italienisch
    'dolce', 'gelato', 'biscotti', 'cannoli',
  ],

  healthy: [
    // Deutsch
    'gesund', 'leicht', 'low carb', 'lowcarb', 'protein', 'salat',
    'gemüse', 'gemuese', 'fit', 'fitness', 'light', 'kalorienarm',
    'fettarm', 'vegan', 'vegetarisch', 'rohkost', 'vitamine', 'superfood',
    'quinoa', 'chia', 'detox', 'clean eating', 'vollkorn', 'ballaststoff',
    'omega', 'nährstoff', 'naehrstoff', 'bio', 'organic',
    // Englisch
    'healthy', 'diet', 'lean', 'green', 'fresh', 'nutritious',
    'whole grain', 'fiber', 'keto', 'paleo', 'gluten free', 'sugar free',
    'plant based', 'wellness', 'slim', 'weight loss',
    // Französisch
    'santé', 'sante', 'léger', 'leger', 'salade',
    // Spanisch
    'saludable', 'ensalada', 'ligero', 'dieta',
    // Italienisch
    'sano', 'insalata', 'leggero',
  ],

  snacks: [
    // Deutsch
    'snack', 'chips', 'wrap', 'sandwich', 'baguette', 'stulle',
    'brot', 'brotzeit', 'vesper', 'jause', 'imbiss', 'häppchen',
    'popcorn', 'dipp', 'dip', 'tapas', 'mezze', 'antipasti',
    'nachos', 'cracker', 'nüsse', 'nuesse', 'erdnuss', 'mandel',
    // Englisch
    'bite', 'nibble', 'appetizer', 'starter', 'small plate',
    'finger food', 'munchies', 'trail mix', 'pretzel', 'nuts',
    // Französisch
    'amuse-bouche', 'apéritif', 'aperitif', 'casse-croûte',
    // Spanisch
    'aperitivo', 'bocadillo', 'tapa', 'pincho',
    // Italienisch
    'spuntino', 'stuzzichino', 'bruschetta', 'crostini',
  ],

  main: [
    // Deutsch
    'hauptgericht', 'hauptspeise', 'mittag', 'mittagessen', 'abendessen',
    'pasta', 'nudeln', 'spaghetti', 'lasagne', 'reis', 'risotto',
    'auflauf', 'gratin', 'curry', 'pizza', 'burger', 'pfanne',
    'eintopf', 'suppe', 'gulasch', 'schnitzel', 'braten', 'steak',
    'fleisch', 'fisch', 'hähnchen', 'haehnchen', 'huhn', 'pute',
    'rind', 'schwein', 'lachs', 'garnele', 'kartoffel', 'pommes',
    'gnocchi', 'ravioli', 'tagliatelle', 'penne', 'fusilli',
    // Englisch
    'main course', 'dinner', 'lunch', 'entrée', 'entree', 'roast',
    'chicken', 'beef', 'pork', 'fish', 'salmon', 'shrimp', 'meat',
    'casserole', 'stew', 'soup', 'noodle', 'rice dish', 'potato',
    // Französisch
    'plat principal', 'dîner', 'diner', 'déjeuner', 'dejeuner',
    'boeuf', 'poulet', 'poisson', 'viande',
    // Spanisch
    'plato principal', 'cena', 'almuerzo', 'carne', 'pollo', 'pescado',
    'arroz', 'paella',
    // Italienisch
    'primo', 'secondo', 'pranzo', 'cena', 'carne', 'pesce', 'pollo',
  ],
};

/**
 * Normalisiert Text für Keyword-Matching
 * - Lowercase
 * - Umlaute ersetzen (ä→ae, ö→oe, ü→ue, ß→ss)
 * - Akzente entfernen
 * - Sonderzeichen entfernen
 */
const normalizeText = (text) => {
  if (!text) return '';

  return text
    .toLowerCase()
    // Deutsche Umlaute
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    // Französische/Spanische Akzente
    .replace(/[àáâãäå]/g, 'a')
    .replace(/[èéêë]/g, 'e')
    .replace(/[ìíîï]/g, 'i')
    .replace(/[òóôõö]/g, 'o')
    .replace(/[ùúûü]/g, 'u')
    .replace(/[ñ]/g, 'n')
    .replace(/[ç]/g, 'c')
    // Sonderzeichen entfernen, aber Leerzeichen behalten
    .replace(/[^a-z0-9\s]/g, ' ')
    // Mehrfache Leerzeichen zusammenfassen
    .replace(/\s+/g, ' ')
    .trim();
};

/**
 * Erkennt die Kategorie eines Rezepts basierend auf Titel, Beschreibung und Tags
 * @param {Object} recipe - Das Rezept-Objekt
 * @returns {string} - Die erkannte Kategorie (z.B. 'breakfast', 'main', etc.)
 */
export const detectRecipeCategory = (recipe) => {
  if (!recipe) return 'main';

  // Text aus allen relevanten Feldern kombinieren
  const textParts = [
    recipe.title || '',
    recipe.description || '',
    recipe.name || '',
    // Tags als Array oder String
    Array.isArray(recipe.tags) ? recipe.tags.join(' ') : (recipe.tags || ''),
    // Zutaten falls vorhanden
    Array.isArray(recipe.ingredients)
      ? recipe.ingredients.map(i => typeof i === 'string' ? i : i.name || '').join(' ')
      : '',
  ];

  const combinedText = normalizeText(textParts.join(' '));

  // Prüfe Kategorien in Prioritätsreihenfolge
  const categoryOrder = ['party', 'breakfast', 'dessert', 'healthy', 'snacks', 'main'];

  for (const category of categoryOrder) {
    const keywords = CATEGORY_KEYWORDS[category];

    for (const keyword of keywords) {
      const normalizedKeyword = normalizeText(keyword);

      // Prüfe ob Keyword im Text vorkommt (als ganzes Wort oder Teil)
      if (combinedText.includes(normalizedKeyword)) {
        return category;
      }
    }
  }

  // Fallback: main
  return 'main';
};

/**
 * Gibt das passende Fallback-Bild für ein Rezept zurück
 * @param {Object} recipe - Das Rezept-Objekt
 * @returns {string} - Der Pfad zum Fallback-Bild
 */
export const getRecipeFallbackImage = (recipe) => {
  const category = detectRecipeCategory(recipe);
  return CATEGORY_IMAGES[category] || CATEGORY_IMAGES.main;
};

/**
 * Gibt das Bild für ein Rezept zurück (Original oder Fallback)
 * @param {Object} recipe - Das Rezept-Objekt
 * @returns {string} - Der Pfad zum Bild
 */
export const getRecipeImage = (recipe) => {
  // Prüfe auf vorhandenes Bild
  if (recipe?.imageUrl) return recipe.imageUrl;
  if (recipe?.image) return recipe.image;
  if (recipe?.thumbnail) return recipe.thumbnail;

  // Fallback-Bild basierend auf Kategorie
  return getRecipeFallbackImage(recipe);
};

// Export für direkten Zugriff auf Bilder (z.B. für onError-Handler)
export const fallbackImages = CATEGORY_IMAGES;
export const defaultImage = mainImage;

export default {
  getRecipeImage,
  getRecipeFallbackImage,
  detectRecipeCategory,
  fallbackImages,
  defaultImage,
};
